<template>
  <div class="pa-3 coverage-comparison-map-page">
    <v-card outlined class="mb-3">
      <v-card-text class="py-3">
        <div class="d-flex justify-space-between align-center flex-wrap">
          <div>
            <div class="subtitle-1 font-weight-bold">Inventory vs submissions coverage</div>
            <div class="caption grey--text text--darken-1">
              Full dataset loaded into memory; the map draws only points in the current view using
              viewport clustering (Supercluster).
            </div>
          </div>
          <div class="d-flex align-center flex-wrap mt-2 mt-md-0">
            <v-btn small text class="mr-1" :to="{ name: 'MobileV2OuternetHeatmap' }">
              <v-icon left small>mdi-arrow-left</v-icon>
              Back to heatmap
            </v-btn>
            <v-btn
              small
              color="primary"
              outlined
              :loading="loadState === 'loading'"
              :disabled="loadState === 'loading'"
              @click="loadCoverage()"
            >
              Refresh
            </v-btn>
          </div>
        </div>

        <v-row dense class="mt-3">
          <v-col cols="6" sm="4" md="3">
            <v-sheet class="kpi-tile kpi-tile--inventory" outlined rounded>
              <div class="caption">Total inventory</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(totals.inventories) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="6" sm="4" md="3">
            <v-sheet class="kpi-tile kpi-tile--submission" outlined rounded>
              <div class="caption">Submissions</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(totals.submissions) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="6" sm="4" md="3">
            <v-sheet class="kpi-tile" outlined rounded>
              <div class="caption grey--text text--darken-1">Submission / inventory</div>
              <div class="text-h6 font-weight-bold">{{ coverageRatioLabel }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="6" sm="4" md="3" v-if="mapPointsLoaded">
            <v-sheet class="kpi-tile" outlined rounded>
              <div class="caption grey--text text--darken-1">On map (this view)</div>
              <div class="text-h6 font-weight-bold">{{ formatNum(visibleMapCount) }}</div>
            </v-sheet>
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-3">
          <v-switch
            v-model="showInventories"
            class="mt-0 mr-4"
            hide-details
            label="Show inventory"
            dense
          />
          <v-switch
            v-model="showSubmissions"
            class="mt-0 mr-4"
            hide-details
            label="Show submissions"
            dense
          />
          <v-switch v-model="isSatellite" class="mt-0" hide-details label="Satellite" dense />
          <v-spacer class="d-none d-sm-block" />
          <v-btn small outlined class="mt-2 mt-sm-0" @click="fitAllData">Fit map</v-btn>
        </div>

        <v-progress-linear
          v-if="indexState === 'building'"
          indeterminate
          color="primary"
          height="3"
          class="mt-3 rounded-pill"
        />
        <div v-if="loadState === 'ok' && mapPointsLoaded" class="caption grey--text mt-2">
          {{ formatNum(mapPointsLoaded) }} points indexed
          <span v-if="visibleMapCount"> · {{ formatNum(visibleMapCount) }} visible in current view</span>
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-if="loadState === 'error'" type="error" dense outlined class="mb-3">
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-else-if="loadState === 'ok' && !mapPointsLoaded"
      type="info"
      dense
      outlined
      class="mb-3"
    >
      No coverage points returned for this view.
    </v-alert>

    <div ref="mapEl" class="map-canvas" />

    <div class="legend-row d-flex flex-wrap align-center mt-3 caption">
      <span class="font-weight-medium mr-3">Legend</span>
      <span class="legend-dot legend-dot--inventory mr-1" />
      <span class="mr-4">Inventory (red)</span>
      <span class="legend-dot legend-dot--submission mr-1" />
      <span class="mr-4">Submissions (green)</span>
      <span class="legend-pie legend-pie--sample mr-1" aria-hidden="true" />
      <span>Clusters: pie = inventory vs submissions; click cluster to zoom in</span>
    </div>
  </div>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2.js";
import {
  INVENTORY_HEX,
  SUBMISSION_HEX,
  buildCoverageClusterSvg,
  createCoverageIndex,
  mapBoundsToBbox,
  pointToFeature,
} from "../../../utils/coverageMapClusters.js";

const service = new RestResource();
const FEATURE_BUILD_CHUNK = 8000;
const MAP_RENDER_DEBOUNCE_MS = 120;
const CLUSTER_FEATURE_KEY = "izcCoverageFeature";

function normalizeCoord(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function isValidLatLng(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

export default {
  name: "CoverageComparisonMapPage",

  data() {
    return {
      loadState: "idle",
      indexState: "idle",
      errorMessage: "",
      inventories: [],
      submissions: [],
      totals: { inventories: 0, submissions: 0 },
      dataBounds: null,
      mapPointsLoaded: 0,
      visibleMapCount: 0,
      map: null,
      clusterIndex: null,
      gMarkers: [],
      mapIdleListener: null,
      mapZoomListener: null,
      renderDebounceTimer: null,
      indexBuildRafId: null,
      infoWindow: null,
      showInventories: true,
      showSubmissions: true,
      isSatellite: false,
    };
  },

  computed: {
    coverageRatioLabel() {
      const inv = this.totals.inventories;
      const sub = this.totals.submissions;
      if (!inv) {
        return sub ? "—" : "0%";
      }
      return `${((sub / inv) * 100).toFixed(1)}%`;
    },
  },

  watch: {
    showInventories() {
      this.rebuildClusterIndex();
    },
    showSubmissions() {
      this.rebuildClusterIndex();
    },
    isSatellite(v) {
      if (this.map) {
        this.map.setMapTypeId(v ? "hybrid" : "roadmap");
      }
    },
  },

  async mounted() {
    await this.loadGoogleMapsScript();
    this.initMap();
    this.bindMapListeners();
    await this.loadCoverage();
  },

  beforeDestroy() {
    this.teardownMapListeners();
    this.clearMapMarkers();
    if (this.infoWindow) {
      this.infoWindow.close();
      this.infoWindow = null;
    }
    this.map = null;
    this.clusterIndex = null;
  },

  methods: {
    formatNum(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return "—";
      }
      return n.toLocaleString();
    },

    parseApiResponse(res) {
      const body = (res && res.data) || {};
      const inner = body.data && typeof body.data === "object" ? body.data : body;
      const rawInv = Array.isArray(inner.inventories) ? inner.inventories : [];
      const rawSub = Array.isArray(inner.submissions) ? inner.submissions : [];

      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;

      const trackBounds = (lat, lng) => {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      };

      const inventories = [];
      for (let i = 0; i < rawInv.length; i += 1) {
        const item = rawInv[i];
        const lat = normalizeCoord(item.lat);
        const lng = normalizeCoord(item.lng);
        const inventoryId = item.inventoryId || item._id || "";
        if (!inventoryId || !isValidLatLng(lat, lng)) continue;
        inventories.push({ inventoryId, lat, lng, color: item.color || "red" });
        trackBounds(lat, lng);
      }

      const submissions = [];
      for (let i = 0; i < rawSub.length; i += 1) {
        const item = rawSub[i];
        const lat = normalizeCoord(item.lat);
        const lng = normalizeCoord(item.lng);
        const submissionId = item.submissionId || item._id || "";
        if (!submissionId || !isValidLatLng(lat, lng)) continue;
        submissions.push({
          submissionId,
          lat,
          lng,
          brand: item.brand || "",
          city: item.city || "",
          state: item.state || "",
          address: item.address || "",
          imageUrl: item.imageUrl || "",
          color: item.color || "green",
        });
        trackBounds(lat, lng);
      }

      const totalInv = Number(inner.totalInventories ?? inner.inventoryTotal);
      const totalSub = Number(inner.totalSubmissions ?? inner.submissionTotal);
      const hasBounds = minLat <= maxLat && minLng <= maxLng;

      return {
        inventories,
        submissions,
        totals: {
          inventories: Number.isFinite(totalInv) ? totalInv : inventories.length,
          submissions: Number.isFinite(totalSub) ? totalSub : submissions.length,
        },
        dataBounds: hasBounds ? { minLat, maxLat, minLng, maxLng } : null,
      };
    },

    safeHttpsUrl(url) {
      if (typeof url !== "string" || !url) {
        return "";
      }
      try {
        const u = new URL(url);
        if (u.protocol !== "https:" && u.protocol !== "http:") {
          return "";
        }
        return url;
      } catch {
        return "";
      }
    },

    async loadCoverage() {
      this.loadState = "loading";
      this.errorMessage = "";
      try {
        const res = await service.getCoverageMap({});
        const parsed = this.parseApiResponse(res);
        this.inventories = parsed.inventories;
        this.submissions = parsed.submissions;
        this.totals = parsed.totals;
        this.dataBounds = parsed.dataBounds;
        this.loadState = "ok";
        await this.rebuildClusterIndex();
        this.$nextTick(() => {
          this.fitAllData();
          this.scheduleMapRender();
        });
      } catch (err) {
        this.inventories = [];
        this.submissions = [];
        this.mapPointsLoaded = 0;
        this.loadState = "error";
        this.errorMessage =
          (err.response && err.response.data && err.response.data.error) ||
          err.message ||
          "Failed to load coverage map.";
      }
    },

    cancelIndexBuild() {
      if (this.indexBuildRafId != null) {
        cancelAnimationFrame(this.indexBuildRafId);
        this.indexBuildRafId = null;
      }
    },

    rebuildClusterIndex() {
      this.cancelIndexBuild();
      this.clearMapMarkers();
      this.clusterIndex = null;
      this.mapPointsLoaded = 0;
      this.visibleMapCount = 0;

      const inv = this.showInventories ? this.inventories : [];
      const sub = this.showSubmissions ? this.submissions : [];
      const total = inv.length + sub.length;

      if (!total) {
        this.indexState = "idle";
        return Promise.resolve();
      }

      this.indexState = "building";
      const features = new Array(total);
      let fi = 0;
      for (let i = 0; i < inv.length; i += 1) {
        features[fi] = pointToFeature(inv[i], "inventory");
        fi += 1;
      }
      for (let i = 0; i < sub.length; i += 1) {
        features[fi] = pointToFeature(sub[i], "submission");
        fi += 1;
      }

      return new Promise(resolve => {
        const index = createCoverageIndex();
        let offset = 0;

        const loadChunk = () => {
          const end = Math.min(offset + FEATURE_BUILD_CHUNK, features.length);
          if (offset === 0) {
            index.load(features.slice(0, end));
          } else {
            index.load(features.slice(offset, end), true);
          }
          offset = end;
          if (offset < features.length) {
            this.indexBuildRafId = requestAnimationFrame(loadChunk);
          } else {
            this.indexBuildRafId = null;
            this.clusterIndex = index;
            this.mapPointsLoaded = total;
            this.indexState = "ready";
            this.scheduleMapRender();
            resolve();
          }
        };

        this.indexBuildRafId = requestAnimationFrame(loadChunk);
      });
    },

    loadGoogleMapsScript() {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const existingScript = document.getElementById("google-maps-script");
        if (existingScript) {
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener(
            "error",
            () => reject(new Error("Failed to load Google Maps script")),
            { once: true }
          );
          return;
        }
        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAP}&v=weekly`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Google Maps script"));
        document.head.appendChild(script);
      });
    },

    initMap() {
      const { google } = window;
      this.map = new google.maps.Map(this.$refs.mapEl, {
        zoom: 6,
        center: { lat: 4.2, lng: 108.0 },
        mapTypeId: "roadmap",
        tilt: 0,
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: false,
        keyboardShortcuts: false,
      });
      this.infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
    },

    bindMapListeners() {
      if (!this.map) return;
      this.mapIdleListener = this.map.addListener("idle", () => this.scheduleMapRender());
      this.mapZoomListener = this.map.addListener("zoom_changed", () => this.scheduleMapRender());
    },

    teardownMapListeners() {
      if (this.renderDebounceTimer) {
        clearTimeout(this.renderDebounceTimer);
        this.renderDebounceTimer = null;
      }
      if (this.mapIdleListener) {
        this.mapIdleListener.remove();
        this.mapIdleListener = null;
      }
      if (this.mapZoomListener) {
        this.mapZoomListener.remove();
        this.mapZoomListener = null;
      }
    },

    scheduleMapRender() {
      if (this.renderDebounceTimer) {
        clearTimeout(this.renderDebounceTimer);
      }
      this.renderDebounceTimer = setTimeout(() => {
        this.renderDebounceTimer = null;
        this.renderViewport();
      }, MAP_RENDER_DEBOUNCE_MS);
    },

    clearMapMarkers() {
      this.gMarkers.forEach(marker => marker.setMap(null));
      this.gMarkers = [];
    },

    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    renderViewport() {
      if (!this.map || !this.clusterIndex || this.indexState !== "ready") {
        return;
      }

      const bounds = this.map.getBounds();
      if (!bounds) {
        return;
      }

      const { google } = window;
      const bbox = mapBoundsToBbox(bounds);
      const zoom = Math.max(0, Math.min(20, Math.floor(this.map.getZoom())));
      const clusters = this.clusterIndex.getClusters(bbox, zoom);

      this.clearMapMarkers();
      this.visibleMapCount = clusters.length;

      clusters.forEach(feature => {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties || {};

        if (props.cluster) {
          const inv = props.invCount || 0;
          const sub = props.subCount || 0;
          const total = props.point_count || inv + sub;
          const url = buildCoverageClusterSvg(total, inv, sub);
          const size = 56;
          const half = size / 2;
          const marker = new google.maps.Marker({
            position: { lat, lng },
            map: this.map,
            icon: {
              url,
              scaledSize: new google.maps.Size(size, size),
              anchor: new google.maps.Point(half, half),
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + total,
            title: `${total} in area — ${inv} inventory, ${sub} submissions`,
          });
          marker.set(CLUSTER_FEATURE_KEY, feature);
          marker.addListener("click", () => this.onClusterClick(marker, feature));
          this.gMarkers.push(marker);
          return;
        }

        const layer = props.layer;
        const point = props.point;
        if (!point || !layer) {
          return;
        }

        const isInventory = layer === "inventory";
        const fillColor = isInventory ? INVENTORY_HEX : SUBMISSION_HEX;
        const marker = new google.maps.Marker({
          position: { lat, lng },
          map: this.map,
          title: isInventory
            ? `Inventory ${point.inventoryId}`
            : `${point.brand || "Submission"} — ${point.city || ""}`.trim(),
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor,
            fillOpacity: isInventory ? 0.85 : 0.92,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
            scale: isInventory ? 5 : 6,
          },
          optimized: true,
          zIndex: isInventory ? 1 : 2,
        });
        marker.set(CLUSTER_FEATURE_KEY, feature);
        marker.addListener("click", () => this.onPointClick(marker, layer, point));
        this.gMarkers.push(marker);
      });
    },

    onClusterClick(marker, feature) {
      if (!this.map || !this.clusterIndex || !feature.properties) {
        return;
      }
      const clusterId = feature.properties.cluster_id;
      const expansionZoom = Math.min(this.clusterIndex.getClusterExpansionZoom(clusterId), 20);
      const [lng, lat] = feature.geometry.coordinates;
      this.map.panTo({ lat, lng });
      this.map.setZoom(Math.max(expansionZoom, (this.map.getZoom() || 0) + 1));
    },

    onPointClick(marker, layer, point) {
      if (!this.infoWindow || !marker) {
        return;
      }

      if (layer === "inventory") {
        const id = this.escapeHtml(point.inventoryId);
        this.infoWindow.setContent(
          `<div style="min-width:160px;font-size:13px;line-height:1.45;">
            <strong>Inventory location</strong><br/>
            <span style="color:${INVENTORY_HEX};font-weight:600;">Not yet submitted</span><br/>
            <span style="color:#78909c;font-size:12px;">ID: ${id}</span>
          </div>`
        );
      } else {
        const brand = this.escapeHtml(point.brand || "—");
        const addr = this.escapeHtml(
          point.address || [point.city, point.state].filter(Boolean).join(", ") || "—"
        );
        const imgSrc = this.safeHttpsUrl(point.imageUrl);
        const thumb = imgSrc
          ? `<div style="margin-top:8px;"><img src="${imgSrc.replace(/"/g, "")}" alt="Billboard" style="max-width:220px;max-height:140px;border-radius:8px;object-fit:cover;" /></div>`
          : "";

        this.infoWindow.setContent(
          `<div style="min-width:180px;max-width:280px;font-size:13px;line-height:1.45;">
            <strong>${brand}</strong><br/>
            <span style="color:${SUBMISSION_HEX};font-weight:600;">Submission</span><br/>
            <span style="color:#455a64;">${addr}</span>
            ${thumb}
          </div>`
        );
      }
      this.infoWindow.open(this.map, marker);
    },

    fitAllData() {
      if (!this.map) {
        return;
      }
      const b = this.dataBounds;
      if (!b) {
        return;
      }
      const { google } = window;
      const bounds = new google.maps.LatLngBounds(
        { lat: b.minLat, lng: b.minLng },
        { lat: b.maxLat, lng: b.maxLng }
      );
      this.map.fitBounds(bounds, 72);
    },
  },
};
</script>

<style scoped>
.map-canvas {
  height: 560px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.kpi-tile {
  padding: 10px 12px;
  height: 100%;
}

.kpi-tile--inventory {
  border-color: #ffcdd2 !important;
  background: linear-gradient(180deg, #ffebee 0%, #fff 100%);
}

.kpi-tile--submission {
  border-color: #c8e6c9 !important;
  background: linear-gradient(180deg, #e8f5e9 0%, #fff 100%);
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #cfd8dc;
}

.legend-dot--inventory {
  background: #e53935;
}

.legend-dot--submission {
  background: #43a047;
}

.legend-pie {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  vertical-align: -2px;
  box-shadow: 0 0 0 1px #cfd8dc;
  background: conic-gradient(#e53935 0deg 200deg, #43a047 200deg 360deg);
}
</style>
